/**
 * Which staff member this device is currently signed in as.
 *
 * Kept in localStorage rather than React state alone because it has two readers that
 * cannot see each other: the picker UI (via StaffIdentityProvider) and
 * ClubBookingApiClient, which stamps every request with the X-Staff-Id header. This is a
 * leaf module on purpose — both import from here, so the key can never drift between the
 * side that writes it and the side that sends it.
 *
 * Device-scoped, not user-scoped: the whole point is that one browser session on a
 * shared reception machine serves a rotating set of people. It survives a refresh (a
 * reception PC is rarely closed) and is cleared on logout.
 */
const STAFF_IDENTITY_KEY = "currentStaffId";

/** The selected staff id, or null when nobody has picked yet or the value is unusable. */
export function readSelectedStaffId(): number | null {
  try {
    const raw = localStorage.getItem(STAFF_IDENTITY_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    // A non-numeric or non-positive leftover means nobody is signed in, not staff id 0 —
    // the API would reject it anyway, so don't send it.
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch {
    // Safari in private mode throws on localStorage access; no selection is a valid state.
    return null;
  }
}

export function writeSelectedStaffId(staffId: number | null): void {
  try {
    if (staffId == null) {
      localStorage.removeItem(STAFF_IDENTITY_KEY);
      return;
    }
    localStorage.setItem(STAFF_IDENTITY_KEY, String(staffId));
  } catch {
    // Non-fatal: the picker still works for this page's lifetime, it just won't persist.
  }
}
